// src/admin/screens/admins/modals/InviteAdminModal.tsx
// Creates an admin_invites row via the admin_invite_create RPC.
// The RPC returns a plaintext token exactly once — after success the
// modal flips to a "copy this link" state. The token is never stored
// in React state beyond the session; closing the modal forgets it.
//
// The invite URL points at /admin/accept-invite/:token which AcceptInvite
// already handles (admin_invite_claim_preview + admin_invite_claim RPCs).
//
// Deviations from the draft plan:
//   - The `admin_invite_create` RPC is shimmed in scripts/gen-types-from-migrations.ts
//     (FUNCTION_EXTRAS) — the real migration is expected from sub-project A.
//     Matches the existing shim pattern used for admin_invite_claim / claim_preview.
//   - Uses the `admin.invite` AuditAction from lib/audit.ts (exists). No
//     `admin.toggle_readonly` / `admin.change_role` on this path — those are
//     for Task 70's detail screen.
//   - Uses the shipped Button (`variant: primary|secondary|ghost|danger`) and
//     FormField (`label`, `htmlFor`, `error`, `hint`, `required`) component
//     APIs rather than the draft plan's speculative surface.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { AdminRole } from '@admin/hooks/useAdmins';
import styles from './InviteAdminModal.module.css';

const ROLES = ['super_admin', 'admin', 'collections'] as const satisfies readonly AdminRole[];

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  display_name: z.string().min(1, 'Required'),
  role: z.enum(ROLES),
  is_readonly: z.boolean(),
});
type Values = z.infer<typeof schema>;

interface Props {
  onClose: (refresh: boolean) => void;
}

export default function InviteAdminModal({ onClose }: Props) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { role: 'admin', is_readonly: false },
  });

  const submit = async (v: Values) => {
    setBusy(true);
    try {
      // RPC returns { id, token } exactly once — we never see the plaintext
      // again after this call.
      const { data, error } = await supabase.rpc('admin_invite_create', {
        p_email: v.email,
        p_display_name: v.display_name,
        p_role: v.role,
        p_is_readonly: v.is_readonly,
      });
      if (error) throw error;
      const payload = data as unknown as { id: string; token: string };
      const base = window.location.origin;
      const url = `${base}/admin/accept-invite/${encodeURIComponent(payload.token)}`;
      await recordAudit({
        action: 'admin.invite',
        target_type: 'admin',
        target_id: payload.id,
        payload: {
          email: v.email,
          role: v.role,
          is_readonly: v.is_readonly,
        },
      });
      setInviteUrl(url);
      toast({
        kind: 'success',
        msg: 'Invite created. Copy the link below and send it to the new admin.',
      });
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  if (inviteUrl) {
    return (
      <Modal open onClose={() => onClose(true)} title="Invite link created">
        <div className={styles.successBody}>
          <p className={styles.lead}>
            Send this link to the new admin. It expires in 48 hours and can only be used once.
          </p>
          <textarea
            readOnly
            value={inviteUrl}
            rows={3}
            className={styles.urlBox}
            onFocus={(e) => e.currentTarget.select()}
          />
          <div className={styles.actions}>
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(inviteUrl);
                  toast({ kind: 'success', msg: 'Copied to clipboard.' });
                } catch {
                  toast({
                    kind: 'error',
                    msg: 'Clipboard write blocked — select the text and copy manually.',
                  });
                }
              }}
            >
              Copy link
            </Button>
            <Button onClick={() => onClose(true)}>Done</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={() => onClose(false)} title="Invite admin">
      <form onSubmit={handleSubmit(submit)} className={styles.form}>
        <FormField
          label="Email"
          htmlFor="invite-email"
          error={errors.email?.message}
          required
        >
          <input
            id="invite-email"
            type="email"
            autoComplete="off"
            {...register('email')}
          />
        </FormField>
        <FormField
          label="Display name"
          htmlFor="invite-name"
          error={errors.display_name?.message}
          required
        >
          <input
            id="invite-name"
            autoComplete="off"
            {...register('display_name')}
          />
        </FormField>
        <FormField
          label="Role"
          htmlFor="invite-role"
          error={errors.role?.message}
          required
        >
          <select id="invite-role" {...register('role')}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          label="Read-only override"
          htmlFor="invite-ro"
          hint="Base role unchanged; RLS blocks all writes. Use for auditors / observers."
        >
          <label className={styles.checkLine}>
            <input id="invite-ro" type="checkbox" {...register('is_readonly')} />
            <span>Block writes regardless of base role</span>
          </label>
        </FormField>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Create invite
          </Button>
        </div>
      </form>
    </Modal>
  );
}
